import React, { useCallback, useState } from "react";
import { graphql, QueryRenderer } from "react-relay";

import { without } from "lodash";
import { commitMutation } from "relay-runtime";

import { ThreeSixtyRelationSingularReverse } from "~/feedback/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import Checkbox from "~/ui/legacy/Checkbox";
import { Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import type { NudgeRequesteesMutation } from "~/__generated__/NudgeRequesteesMutation.graphql";
import type { NudgeRequesteesQuery, NudgeRequesteesQuery$data } from "~/__generated__/NudgeRequesteesQuery.graphql";

import styles from "./css/NudgeRequestees.module.css";

const NudgeRequestees = graphql`
	mutation NudgeRequesteesMutation($id: ID!, $requestees: [ID]!) {
		nudgeRequestees(id: $id, requestees: $requestees) {
			requests {
				id
			}
		}
	}
`;

interface Props {
	id: string;
	selected?: boolean | string[];
	onClose: () => void;
}

function NudgeModal({ id: feedbackID, node, selected, onClose }: NudgeRequesteesQuery$data & Props) {
	if (!node) throw new Error("Feedback node in nudge modal is undefined");
	let { requests } = node;
	if (requests === undefined) throw new Error("Requests on feedback node is undefined");

	requests = requests.filter(({ feedbackRecord }) => !feedbackRecord);

	let selectedRequestees: string[] = [];
	if (selected instanceof Array) {
		selectedRequestees = selected;
	} else if (selected) {
		selectedRequestees = requests.map(({ id }) => id);
	}

	const [selection, setSelection] = useState(selectedRequestees);
	const [nudged, setNudged] = useState(false);

	const allChecked = requests.length === selection.length;

	const toggleAll = useCallback(() => {
		if (requests === undefined) return;

		if (allChecked) {
			setSelection([]);
		} else {
			setSelection(requests.map(({ id }) => id));
		}
	}, [allChecked, requests]);

	function toggleRequest(id: string) {
		return () => {
			if (selection.includes(id)) {
				setSelection(without(selection, id));
			} else {
				setSelection([...selection, id]);
			}
		};
	}

	const nudgeSelection = useCallback(() => {
		setNudged(true);
		commitMutation<NudgeRequesteesMutation>(environment, {
			mutation: NudgeRequestees,
			variables: {
				id: feedbackID,
				requestees: selection,
			},
			onCompleted: ({ nudgeRequestees }, err) => {
				setNudged(false);
				if (err) toast.error(`Unable to nudge requestees: ${err[0].message}`);
				if (nudgeRequestees) {
					const count = nudgeRequestees.requests.length;
					toast.success(`Successfully sent ${count} ${count > 1 ? "reminders" : "reminder"}!`);
					onClose();
				}
			},
		});
	}, [feedbackID, onClose, selection]);

	const isAnonymous = node.isAnonymous;
	let message = `${requests.length} ${requests.length === 1 ? "person has" : "people have"} not responded`;
	if (isAnonymous) {
		message = `${message}. Responses are anonymous, nudge all remaining respondents?`;
	}

	return (
		<Modal onClose={onClose} width="25.5rem">
			<Modal.Header onClose={onClose}>
				<Modal.Title>Send a reminder to</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<div className={styles.toggleAll}>
					<div>{message}</div>
					{/* HACK: key is required in order for react to re-render the checkbox when `checked` changes */}
					{!isAnonymous && (
						<Checkbox
							key={allChecked.toString()}
							label="Toggle all requestees"
							checked={allChecked}
							onChange={toggleAll}
							className={styles.checkbox}
						/>
					)}
				</div>
				{!isAnonymous &&
					requests.map(({ id, user, feedback: { threeSixtyRelation } }) => {
						const checked = selection.includes(id);
						const relation = threeSixtyRelation
							? ThreeSixtyRelationSingularReverse[
									threeSixtyRelation.toString(10) as keyof typeof ThreeSixtyRelationSingularReverse
								]
							: null;

						return (
							<div key={id} className={styles.request}>
								<div className={styles.user}>
									<Avatar user={user} link={false} className={styles.userImg} />
									{relation ? (
										<div className={styles.userThreeSixtyContainer}>
											<div>{user.fullName}</div>
											<p className={styles.userThreeSixtyRelation}>{relation}</p>
										</div>
									) : (
										<div>{user.fullName}</div>
									)}
								</div>
								{/* HACK: key is required in order for react to re-render the checkbox when `checked` changes */}
								<Checkbox
									key={checked.toString()}
									label={`Toggle ${user.fullName}`}
									checked={checked}
									onChange={toggleRequest(id)}
									className={styles.checkbox}
								/>
							</div>
						);
					})}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" disabled={selection.length === 0 || nudged} onClick={nudgeSelection}>
					Send reminder ({selection.length})
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default function NudgeQueryRenderer(outerProps: Props) {
	return (
		<QueryRenderer<NudgeRequesteesQuery>
			environment={environment}
			query={graphql`
				query NudgeRequesteesQuery($id: ID!) {
					node(id: $id) {
						... on FeedbackNode {
							isAnonymous
							requests {
								id

								feedbackRecord {
									id
								}

								feedback {
									threeSixtyRelation
								}

								user {
									id
									email
									image
									fullName
									firstName
									lastName
									isGuest
								}
							}
						}
					}
				}
			`}
			variables={{ id: outerProps.id }}
			// eslint-disable-next-line react/jsx-no-bind
			render={({ props }) => props && <NudgeModal {...outerProps} {...props} />}
		/>
	);
}
